import { apiGet, apiPost } from '@/shared/utils/requestBuilder';

import type {
  CreateProcessoArbitralPayload,
  CreateProcessoArbitralResponse,
  ProcessoInicioFluxoStatus,
} from '@/features/processo-arbitral/types/submissaoStepTypes';

export class SubmissaoApiError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'SubmissaoApiError';
    this.code = code;
  }
}

const handleApiError = async (response: Response, defaultMessage: string): Promise<never> => {
  const body = (await response.json().catch(() => null)) as unknown;

  if (typeof body === 'object' && body) {
    const record = body as Record<string, unknown>;
    const directMessage = record.message;
    const directError = record.error;
    const directCode = typeof record.code === 'string' ? record.code : undefined;

    if (typeof directMessage === 'string' && directMessage.trim()) {
      throw new SubmissaoApiError(directMessage, directCode);
    }

    if (typeof directError === 'string' && directError.trim()) {
      throw new SubmissaoApiError(directError, directCode);
    }

    if (typeof directError === 'object' && directError) {
      const nestedError = directError as Record<string, unknown>;
      const nestedMessage = nestedError.message;
      const nestedCode = nestedError.code;

      if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
        throw new SubmissaoApiError(
          nestedMessage,
          typeof nestedCode === 'string' ? nestedCode : directCode,
        );
      }
    }
  }

  throw new SubmissaoApiError(defaultMessage);
};

const unwrapSuccessEnvelope = <T>(json: unknown): T => {
  if (typeof json === 'object' && json && 'success' in json) {
    const typedJson = json as { success: boolean; data?: T; error?: { message?: string } };

    if (typedJson.success) {
      return typedJson.data as T;
    }

    throw new Error(typedJson.error?.message || 'Erro ao processar requisição');
  }

  return json as T;
};

export const submissaoStepService = {
  createProcessoArbitral: async (
    payload: CreateProcessoArbitralPayload,
  ): Promise<CreateProcessoArbitralResponse> => {
    const response = await apiPost('/arbitral/processos', payload);

    if (!response.ok) {
      await handleApiError(response, 'Erro ao enviar arbitragem');
    }

    const body = (await response.json()) as unknown;
    return unwrapSuccessEnvelope<CreateProcessoArbitralResponse>(body);
  },
  getProcessoInicioFluxo: async (processoId: string): Promise<ProcessoInicioFluxoStatus> => {
    const response = await apiGet(`/arbitral/processos/${processoId}`);

    if (!response.ok) {
      await handleApiError(response, 'Erro ao consultar o início do fluxo');
    }

    const body = (await response.json()) as unknown;
    const detail = unwrapSuccessEnvelope<{ inicioFluxo?: ProcessoInicioFluxoStatus }>(body);

    return detail.inicioFluxo ?? { falhou: false, falhouEm: null, erro: null };
  },
};
